import { useContext } from 'react';
import SectionWrapper from './SectionWrapper';
import { Text, Box, ListItem, UnorderedList, Divider } from '@chakra-ui/react';
import TemplateContentField from './TemplateContentField';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import { addField } from '../../../store/redux/slices/template.slice';
import { FIELD_CONFIG, SLOT_LABELS } from './utils/fieldVisibilityConfig';
import {
  useTemplateFieldValidation,
  useInitialDuplicateValidation,
} from './validation/useTemplateFieldValidation';
import AddMoreButton from './AddMoreButton';
import { MeContext } from '../../../context/MeContext';
import { MeContextState } from '../../../store/types/account';
import AdditionalFieldsSection from './AdditionalFieldsSection';

const BackFields = ({
  isShared,
  isEdit,
}: {
  isShared: boolean;
  isEdit: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { state: meState } = useContext(MeContext) as { state: MeContextState };
  const expressivePassRolloutEnabled =
    meState?.expressivePassRolloutEnabled === true;
  const templateType = useAppSelector((s) => s.template.templateType);
  const backFields = useAppSelector((s) => s.template.fields.back);
  const fields = useAppSelector((s) => s.template.fields);
  const config = FIELD_CONFIG[templateType];
  const backLimit = config.back.limit;
  const minRequired = config.back.minRequired;
  const { validateField } = useTemplateFieldValidation('fields');
  const errors = useAppSelector((s) => s.templateErrors);

  useInitialDuplicateValidation(
    ['back'],
    fields,
    validateField,
    expressivePassRolloutEnabled
  );

  if (expressivePassRolloutEnabled) {
    return <AdditionalFieldsSection isShared={isShared} isEdit={isEdit} />;
  }

  const isBackLimitReached =
    Object.values(backFields).filter((field) => field !== null).length >=
    backLimit;
  const isMinRequiredReached =
    Object.values(backFields).filter((field) => field !== null).length >
    minRequired;

  return (
    <SectionWrapper
      id="fields-section"
      title="Content"
      tooltip={
        <Text textAlign="left">
          These fields appear on the back of your pass. Their values can also
          include clickable links (for example: websites, phone numbers, or
          email addresses). Each field includes:
          <UnorderedList>
            <ListItem>
              an ID used internally to identify the field – required
            </ListItem>
            <ListItem>a label that’s shown on the pass – optional</ListItem>
            <ListItem>
              a default value for static content that stays the same for all
              users – optional
            </ListItem>
          </UnorderedList>
        </Text>
      }
      openOnLoad={true}
      subtitle={
        !isShared &&
        "Back fields are auto-populated from Shared. These values apply to all platforms and can't be changed per device."
      }
    >
      {/* BACK FIELDS */}
      <Box>
        {Object.entries(backFields)
          .filter(([, field]) => field !== null)
          .map(([slot], fieldIndex) => (
            <Box key={slot}>
              {fieldIndex > 0 && <Divider borderColor="lightGrey" my={4} />}
              <TemplateContentField
                label={`Back Field ${SLOT_LABELS[slot] ?? slot}`}
                section="back"
                slot={slot}
                newlyAdded={backFields[slot]._newlyAdded}
                validateField={validateField}
                errors={errors}
                isMinRequiredReached={isMinRequiredReached}
                isShared={isShared}
                isEdit={isEdit}
                autoGenerateFieldId
              />
            </Box>
          ))}

        {backLimit > 0 && <Divider borderColor="lightGrey" my={4} />}
        {backLimit > 0 && !isBackLimitReached && isShared && (
          <Box mt={0}>
            <AddMoreButton
              onClick={() => dispatch(addField({ section: 'back' }))}
              text="Add back field"
            />
          </Box>
        )}
      </Box>
    </SectionWrapper>
  );
};

export default BackFields;
